import {Requester} from '@getgo/g2r-shared-components';
import * as React from 'react';
import { API_URL } from '../Constants';
import {createLogger} from '../helpers/Logger';
import {getHeaders} from '../helpers/Utils';
import {AccountProps, CurrentUser} from './SettingsKeeper';

const logger = createLogger('AccountDetailKeeper');

export interface AccountDetailKeeperProps extends React.ComponentPropsWithRef<'div'> {
    accounts: AccountProps[];
    currentUser: CurrentUser;
}

export interface License {
    key: string;
    seats: number;
    userCount: number;
    enabled: boolean;
    description: string;
    type: string;
    channel: string;
    deviceKeys: string[];
    deviceCount: number;
    products: string[];
    entitlements: any;
    devicesOnly: boolean;
    externallyManaged: boolean;
}

export interface LicenseResponseProps {
   results: License[];
}

export interface RegistrationResponseProps {
    key: string;
    email: string;
}

export interface AccountDetailKeeperState {
    currentAccountKey: string;
    setCurrentAccountKey: (k: string) => void;
    activationStatus: string;
    activationCode: string;
    setActivationCode: (c: string) => void;
    activateDevice: (code: string) => void;
    hasLicense: boolean;
    loading: boolean;
    userHasAccess: boolean;
    seatAvailable: boolean;
    errorCode: string;
    setActivationStatus: (status: string) => void;
}

export const AccountDetailContext = React.createContext<AccountDetailKeeperState>(
    {
      currentAccountKey: '',
      setCurrentAccountKey: undefined,
      activationStatus: undefined,
      activateDevice: undefined,
      activationCode: '',
      setActivationCode: undefined,
      hasLicense: false,
      loading: false,
      userHasAccess: false,
      seatAvailable: true,
      errorCode: null,
      setActivationStatus: undefined,
    },

);

export const AccountDetailKeeper = ({currentUser, accounts, children}: AccountDetailKeeperProps) => {
    const [currentAccountKey, setCurrentAccountKey] = React.useState(accounts[0].key);
    const [activationStatus, setActivationStatus] = React.useState('pending');
    const [seatAvailable, setSeatAvailable] = React.useState(true);
    const [hasLicense, setHasLicense] = React.useState(null);
    const [licenseKey, setLicenseKey] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [activationCode, setActivationCode] = React.useState('');
    const [userHasAccess, setUserHasAccess] = React.useState(null);
    const [errorCode, setErrorCode] = React.useState(null);

    React.useEffect(() => {
        getLicense(currentAccountKey);
    }, [currentAccountKey]);

    const isSystemUer = () =>  (currentUser.systemRoles &&
        currentUser.systemRoles.includes('SYSTEM_USER') ||
        currentUser.adminRoles && currentUser.adminRoles.includes('SYSTEM_USER'));

    const isSuperUser = () =>  (accounts.length === 1 &&
        (currentUser.systemRoles && currentUser.systemRoles.includes('SUPER_USER')) ||
        (currentUser.adminRoles && currentUser.adminRoles.includes('SUPER_USER'))
    );

    const hasAccountAccess =  (accountKey) =>  (accounts.length > 1 && currentUser.accounts &&
      currentUser.accounts[accountKey] &&
      currentUser.accounts[accountKey].adminRoles.includes('SUPER_USER'));

    const getLicense = React.useCallback(async (accountKey) => {
        const userAccess =  isSystemUer() || isSuperUser() || hasAccountAccess(accountKey);
        setUserHasAccess(!!userAccess);
        if (userAccess) {
          await Requester.request(API_URL, `/ext-admin/rest/accounts/${currentAccountKey}/licenses?filter=enabled eq "true" & devicesOnly eq "true"`, {
              method: 'GET',
              headers: getHeaders(),
          }, logger).then((resp: LicenseResponseProps) => {
              if (resp && resp.results) {
                  const availableLicenses = resp.results.filter( (license) => {
                      return ['GoToConference', 'GoToRoom'].includes(license.description);
                  });
                  const licenseWithSeats = availableLicenses.find((license) => license.seats > license.deviceCount);
                  const roomLicense = licenseWithSeats ?? availableLicenses[0];
                  const hasSeats = (roomLicense?.seats && roomLicense.seats > roomLicense.deviceCount);
                  setHasLicense(!!roomLicense);
                  setSeatAvailable(!!hasSeats);
                  setLicenseKey(!!hasSeats ? roomLicense.key : null);
              }
          });
      }
    }, [currentAccountKey]);

    const activateDevice = async (deviceCode) => {
        if (loading) { return; }
        setLoading(true);
        setErrorCode(null);
        const licenseKeys = licenseKey ? [licenseKey] : undefined;
        await Requester.request(API_URL, `/ext-admin/rest/accounts/${currentAccountKey}/devices`,
            {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ deviceCode, licenseKeys}) }, logger)
            .then((response: RegistrationResponseProps) => {
                setLoading(false);
                setActivationStatus('success');
            }).catch((error) => {
                setLoading(false);
                logger.info(`Request Device Registration failed for account: ${currentAccountKey}, error: ${JSON.stringify(error)}`);
                setActivationStatus('error');

                if (error && error.errors && error.errors.length && error.errors[0].code) {
                    setErrorCode(error.errors[0].code);
                } else {
                    setErrorCode('unknown');
                }

            });
    };

    const providerValues = { currentAccountKey, setCurrentAccountKey, activateDevice,
        activationStatus, activationCode, setActivationCode, hasLicense, loading, seatAvailable,
        userHasAccess, errorCode, setActivationStatus,
    };

    return (
        <AccountDetailContext.Provider value={{...providerValues}}>
          {children}
        </AccountDetailContext.Provider>
    );
};
